import axios from "axios";
import { Token } from "../utils/localStorage.util";
import config from "../config.js";

const apiRelatorios = axios.create({
  baseURL: config.REACT_APP_HOST_API_RELATORIOS,
});

apiRelatorios.interceptors.request.use(
  async (config) => {
    const token = Token.getToken();
    if (token) {
      config.headers.Authorization = token;
    }

    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiRelatorios.interceptors.response.use(
  (response) => {
    return response.data;
  },

  async function (error) {
    if (error?.code === "ERR_NETWORK") {
      console.error("Erro de conexão (ERR_NETWORK):", error);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Lista relatórios. Sem parâmetros: lista completa (cadastro).
 * Com `limit` (e opcionalmente `page`, `q`): resposta paginada com `total`, `page`, `limit`.
 * @param {{ page?: number, limit?: number, q?: string, apuracao?: boolean }} [params]
 */
export function buscarRelatorios(params) {
  if (params == null || typeof params !== "object") {
    return apiRelatorios.get(`/relatorios`);
  }
  const sp = new URLSearchParams();
  if (params.page != null) sp.set("page", String(params.page));
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.q != null && String(params.q).trim() !== "") {
    sp.set("q", String(params.q).trim());
  }
  if (params.apuracao === true) {
    sp.set("apuracao", "true");
  } else if (params.apuracao === false) {
    sp.set("apuracao", "false");
  }
  const qs = sp.toString();
  return apiRelatorios.get(qs ? `/relatorios?${qs}` : `/relatorios`);
}

/**
 * @deprecated Preferir {@link buscarRelatorios} com `{ apuracao: false, page, limit }`.
 * Listagem paginada para a página Extrator (`GET /relatorios?apuracao=false&page=&limit=`).
 * @param {{ page?: number, limit?: number }} [params]
 */
export function buscarRelatoriosExtrator(params) {
  const base =
    params != null && typeof params === "object"
      ? { apuracao: false, page: params.page, limit: params.limit }
      : { apuracao: false };
  return buscarRelatorios(base);
}

export function buscarRelatoriosPorTexto(textoDeBusca) {
  return apiRelatorios.get(
    `/relatorios/buscar-por-texto?texto=${textoDeBusca}`
  );
}

export function buscarRelatoriosPorCodigo(codigo) {
  return apiRelatorios.get(`/relatorios/buscar-por-codigo/${codigo}`);
}

/**
 * Listagem paginada de apurações (GET /relatorios/apuracoes).
 * @param {{ page?: number, limit?: number, q_empresa?: string, cnpj?: string, tipo?: string, status?: string }} [params]
 * @returns {Promise<{ sucesso: boolean, itens: object[], total: number, page?: number, limit?: number, mensagem?: string }>}
 */
export function listarApuracoes(params) {
  const sp = new URLSearchParams();
  if (params?.page != null) sp.set("page", String(params.page));
  if (params?.limit != null) sp.set("limit", String(params.limit));
  if (params?.q_empresa != null && String(params.q_empresa).trim() !== "") {
    sp.set("q_empresa", String(params.q_empresa).trim());
  }
  if (params?.cnpj != null && String(params.cnpj).trim() !== "") {
    sp.set("cnpj", String(params.cnpj).replace(/\D/g, ""));
  }
  if (params?.tipo != null && String(params.tipo).trim() !== "") {
    sp.set("tipo", String(params.tipo).trim());
  }
  if (params?.status != null && String(params.status).trim() !== "") {
    sp.set("status", String(params.status).trim());
  }
  const qs = sp.toString();
  return apiRelatorios.get(
    qs ? `/relatorios/apuracoes?${qs}` : "/relatorios/apuracoes"
  );
}

/**
 * Validações em tempo real para a modal Nova apuração (GET /relatorios/apuracoes/validar-nova).
 * @param {{ cnpj: string, idRelatorio?: number, codigo_relatorio?: string, periodo_inicio: string, periodo_fim: string }} params
 */
export function validarNovaApuracao(params) {
  const sp = new URLSearchParams();
  if (params?.cnpj != null) {
    sp.set("cnpj", String(params.cnpj).replace(/\D/g, ""));
  }
  if (params?.idRelatorio != null) {
    sp.set("idRelatorio", String(params.idRelatorio));
  }
  if (params?.codigo_relatorio != null && String(params.codigo_relatorio).trim() !== "") {
    sp.set("codigo_relatorio", String(params.codigo_relatorio).trim());
  }
  if (params?.periodo_inicio != null) {
    sp.set("periodo_inicio", String(params.periodo_inicio).trim());
  }
  if (params?.periodo_fim != null) {
    sp.set("periodo_fim", String(params.periodo_fim).trim());
  }
  // Timeout explícito: evita botão travado indefinidamente na validação em tempo real (debounce).
  return apiRelatorios.get(
    `/relatorios/apuracoes/validar-nova?${sp.toString()}`,
    { timeout: 10000 }
  );
}

/**
 * Detalhe da apuração + processamentos na fila (GET /relatorios/apuracoes/:id).
 * @param {string|number} id
 */
export function buscarApuracaoPorId(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}`);
}

/**
 * Lançamentos paginados de uma conta (GET /relatorios/apuracoes/:id/lancamentos).
 * @param {string|number} id
 * @param {object} params
 */
export function buscarLancamentosContaApuracao(id, params = {}) {
  const sp = new URLSearchParams();
  const ecd = String(params.ecdContaContabil ?? params.ecd ?? "").trim();
  if (ecd) sp.set("ecdContaContabil", ecd);
  if (params.pagina != null) sp.set("page", String(params.pagina));
  if (params.limite != null) sp.set("limit", String(params.limite));
  // TXM-558 — drill-down dos itens utilizados de um projeto aprovado (somente leitura).
  if (params.projeto) sp.set("projeto", String(params.projeto));

  const uiKeys = [
    "competenciaInicio",
    "competenciaFim",
    "buscaConta",
    "baseMin",
    "baseMax",
    "pisMin",
    "pisMax",
    "cofinsMin",
    "cofinsMax",
  ];
  for (const key of uiKeys) {
    const v = params.filtrosUi?.[key];
    if (v != null && String(v).trim() !== "") sp.set(key, String(v).trim());
  }
  if (Array.isArray(params.filtrosUi?.contasSelecionadas) && params.filtrosUi.contasSelecionadas.length) {
    sp.set("contasSelecionadas", params.filtrosUi.contasSelecionadas.join(","));
  }
  // TT#10085 — baseLegal virou multi-select (baseLegalSelecionadas); espelha contasSelecionadas.
  if (Array.isArray(params.filtrosUi?.baseLegalSelecionadas) && params.filtrosUi.baseLegalSelecionadas.length) {
    sp.set("baseLegalSelecionadas", params.filtrosUi.baseLegalSelecionadas.join(","));
  }
  if (Array.isArray(params.filtrosDinamicos) && params.filtrosDinamicos.length) {
    sp.set("filtrosDinamicos", JSON.stringify(params.filtrosDinamicos));
  }

  return apiRelatorios.get(`/relatorios/apuracoes/${id}/lancamentos?${sp.toString()}`);
}

/**
 * Dataset integral da sumarização (GET /relatorios/apuracoes/:id/sumarizacao).
 * Sem `params.projeto` os itens já utilizados em projetos aprovados ficam de fora; com ele,
 * a API devolve só os itens daquele projeto (TXM-558).
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [params]
 */
export function buscarSumarizacaoApuracao(id, params) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao`, {
    params: params?.projeto ? { projeto: params.projeto } : undefined,
  });
}

/**
 * TXM-558 — Projetos de Apuração (api-relatorios, módulo apuracao).
 * Todas devolvem o corpo da resposta (o interceptor já extrai `response.data`).
 */

/** POST /relatorios/projetos-apuracao — cria ou sobrescreve um projeto Em Prévia. */
export function salvarProjetoApuracao(body) {
  return apiRelatorios.post("/relatorios/projetos-apuracao", body);
}

/**
 * GET /relatorios/projetos-apuracao — lista paginada.
 * @param {{ page?: number, limit?: number, idProjeto?: string, razaoSocial?: string, cnpj?: string, tipo?: string, status?: string }} [params]
 */
export function listarProjetosApuracao(params) {
  const sp = new URLSearchParams();
  if (params?.page != null) sp.set("page", String(params.page));
  if (params?.limit != null) sp.set("limit", String(params.limit));
  ["idProjeto", "razaoSocial", "cnpj", "tipo", "status"].forEach((chave) => {
    const valor = params?.[chave];
    if (valor != null && String(valor).trim() !== "") sp.set(chave, String(valor).trim());
  });
  const qs = sp.toString();
  return apiRelatorios.get(qs ? `/relatorios/projetos-apuracao?${qs}` : "/relatorios/projetos-apuracao");
}

/** GET /relatorios/projetos-apuracao/:id — resumo do projeto. */
export function buscarProjetoApuracao(id) {
  return apiRelatorios.get(`/relatorios/projetos-apuracao/${id}`);
}

/**
 * GET /relatorios/projetos-apuracao/por-cliente — "Projetos deste cliente".
 * @param {{ cnpj: string, tipo: string }} params
 */
export function projetosApuracaoPorCliente({ cnpj, tipo }) {
  return apiRelatorios.get("/relatorios/projetos-apuracao/por-cliente", {
    params: { cnpj: String(cnpj).replace(/\D/g, ""), tipo },
  });
}

/**
 * POST /relatorios/projetos-apuracao/:id/:acao
 * @param {string|number} id
 * @param {'atualizar-valores'|'enviar'|'liberar'|'cancelar'|'aprovar'|'reenviar-creditos'} acao
 */
export function acaoProjetoApuracao(id, acao) {
  return apiRelatorios.post(`/relatorios/projetos-apuracao/${id}/${acao}`);
}

/**
 * TXM-418 — sumarização das apurações de ICMS (motores icmsapuracao/icmsstcat42).
 * Endpoint separado do genérico porque o layout de linha é outro (sem conta
 * contábil, base, Selic ou PIS/COFINS) — mesmo padrão que o eSocial.
 * @param {string|number} id
 */
export function buscarSumarizacaoIcmsApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-icms`);
}

/**
 * TXM-418/TXM-597 — sumarização da apuração de Notas Fiscais (competência + tipo).
 * Endpoint separado pelo mesmo motivo do ICMS: layout de linha próprio.
 * @param {string|number} id
 */
export function buscarSumarizacaoNotasFiscaisApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-notas-fiscais`);
}

/**
 * TXM-597 — lançamentos individuais (1 linha por documento) de uma competência+tipo
 * (GET /relatorios/apuracoes/:id/sumarizacao/lancamentos-notas-fiscais).
 * @param {string|number} id
 * @param {string} competencia YYYY-MM
 * @param {string} tipo "Entrada"|"Saída"
 */
export function buscarLancamentosNotasFiscaisApuracao(id, competencia, tipo) {
  const sp = new URLSearchParams({ competencia, tipo });
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/sumarizacao/lancamentos-notas-fiscais?${sp.toString()}`
  );
}

/**
 * TXM-418/TXM-598 — sumarização da apuração ECD (razão contábil por conta e competência).
 * Endpoint separado pelo mesmo motivo do Notas Fiscais: layout de linha próprio.
 * @param {string|number} id
 */
export function buscarSumarizacaoEcdApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-ecd`);
}

/**
 * TXM-598 — lançamentos individuais (I200+I250) de uma conta contábil numa competência
 * (GET /relatorios/apuracoes/:id/sumarizacao/lancamentos-ecd). Paginado — o bruto de
 * origem chega a dezenas de milhões de linhas por CNPJ.
 * @param {string|number} id
 * @param {{ competencia: string, conta: string, natureza?: string, busca?: string, pagina?: number, pageSize?: number }} params
 */
export function buscarLancamentosEcdApuracao(id, params) {
  const sp = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    )
  );
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/sumarizacao/lancamentos-ecd?${sp.toString()}`
  );
}

/**
 * TXM-599 — sumarização da apuração ECF (apuração anual do IRPJ/CSLL, grão ano)
 * (GET /relatorios/apuracoes/:id/sumarizacao-ecf).
 * @param {string|number} id
 */
export function buscarSumarizacaoEcfApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-ecf`);
}

/**
 * TXM-473 — sumarização da apuração ICMS-ST (gerencial): cruzamento entrada×saída por
 * competência/NCM/EAN (GET /relatorios/apuracoes/:id/sumarizacao-icmsst).
 * @param {string|number} id
 */
export function buscarSumarizacaoIcmsStApuracao(id, params) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-icmsst`, {
    params: params?.projeto ? { projeto: params.projeto } : undefined,
  });
}

/**
 * TXM-600 — sumarização da apuração Contribuições (blocos M210/M200/M610/M600/F100/C170
 * do SPED Contribuições, por competência)
 * (GET /relatorios/apuracoes/:id/sumarizacao-contribuicoes).
 * @param {string|number} id
 */
export function buscarSumarizacaoContribuicoesApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-contribuicoes`);
}

/**
 * TXM-418/TXM-475 — sumarização da apuração Ecredac Simplificada (e-CredAc, Portaria CAT
 * 207/2009), N linhas por competência (1 por categoria de operação geradora + 1 linha SOMA)
 * (GET /relatorios/apuracoes/:id/sumarizacao-ecredac).
 * @param {string|number} id
 */
export function buscarSumarizacaoEcredacApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao-ecredac`);
}

/**
 * TXM-418/TXM-475 — créditos por nota do Ecredac Simplificado (grão do card/protótipo)
 * (GET /relatorios/apuracoes/:id/ecredac-notas).
 * @param {string|number} id
 */
export function buscarNotasEcredacApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/ecredac-notas`);
}

/**
 * TXM-418/TXM-475 — exporta "Créditos por nota" (com os filtros da tela) direto pra pasta
 * relatorios, sem download no navegador (POST /relatorios/apuracoes/:id/ecredac-notas/exportar).
 * @param {string|number} id
 * @param {{cnpj?: string, busca?: string, competencia?: string, fluxo?: string, credito?: 'SIM'|'NAO'}} filtros
 */
export function exportarNotasEcredacApuracao(id, filtros) {
  return apiRelatorios.post(`/relatorios/apuracoes/${id}/ecredac-notas/exportar`, { filtros });
}

/**
 * TXM-599 — lançamentos individuais de ajuste (M300, Adição/Exclusão) do fechamento anual
 * de um exercício (GET /relatorios/apuracoes/:id/sumarizacao/lancamentos-ecf). Paginado.
 * @param {string|number} id
 * @param {{ ano: number, tipoLancamento?: string, busca?: string, pagina?: number, pageSize?: number }} params
 */
export function buscarLancamentosEcfApuracao(id, params) {
  const sp = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    )
  );
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/sumarizacao/lancamentos-ecf?${sp.toString()}`
  );
}

/**
 * TXM-600 — lançamentos de um bloco do SPED Contribuições numa competência
 * (GET /relatorios/apuracoes/:id/sumarizacao/lancamentos-contribuicoes). Paginado.
 * @param {string|number} id
 * @param {{ competencia: string, bloco?: string, busca?: string, valorMin?: number, valorMax?: number, pagina?: number, pageSize?: number }} params
 */
export function buscarLancamentosContribuicoesApuracao(id, params) {
  const sp = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    )
  );
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/sumarizacao/lancamentos-contribuicoes?${sp.toString()}`
  );
}

/**
 * TXM-472 — NCMs da apuração CAT-42 com alíquota interna, MVA, Tipo e Classificação sugerida
 * da Tabela NCM (TXM-594, via public.fn_tb_ncm_item_vigente)
 * (GET /relatorios/apuracoes/:id/sumarizacao/classificacao-ncm).
 * @param {string|number} id id da apuração CAT-42 (ICMS_ST_CAT42), não da apuração ICMS pai
 */
export function buscarClassificacaoNcmApuracao(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao/classificacao-ncm`);
}

/**
 * TXM-479/TXM-594 — mapa NCM → Classificação sugerida (Insumo/Uso/Consumo) pra tela de
 * Lançamentos Ecredac (GET /relatorios/apuracoes/:id/sumarizacao/classificacao-ecredac-ncm).
 * @param {string|number} id mesma apuração Ecredac de buscarNotasEcredacApuracao
 */
export function buscarClassificacaoSugeridaEcredac(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/sumarizacao/classificacao-ecredac-ncm`);
}

/**
 * Lançamentos (entrada+saída) de uma competência CAT-42, por item
 * (GET /relatorios/apuracoes/:id/sumarizacao/lancamentos-cat42).
 * @param {string|number} id
 * @param {string} competencia YYYY-MM
 */
export function buscarLancamentosCat42Apuracao(id, competencia) {
  const sp = new URLSearchParams({ competencia });
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/sumarizacao/lancamentos-cat42?${sp.toString()}`
  );
}

/**
 * TXM-476 — ajustes E111 (+ indicação de E112) de uma competência da apuração ICMS
 * (GET /relatorios/apuracoes/:id/sumarizacao/lancamentos-icms-e111).
 * @param {string|number} id
 * @param {string} competencia YYYY-MM
 */
export function buscarLancamentosIcmsE111Apuracao(id, competencia) {
  const sp = new URLSearchParams({ competencia });
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/sumarizacao/lancamentos-icms-e111?${sp.toString()}`
  );
}

/**
 * Aplica a base legal a todas as competências de uma conta (PATCH).
 * `compoe_projeto` deriva no backend — não é enviado daqui.
 * @param {string|number} id
 * @param {{ ecdContaContabil: string, baseLegalId: number|null }} body
 */
export function atualizarBaseLegalSumarizacao(id, body) {
  return apiRelatorios.patch(
    `/relatorios/apuracoes/${id}/sumarizacao/base-legal`,
    body
  );
}

/**
 * Catálogo de bases legais (GET /relatorios/bases-legais).
 * @param {"CONCILIACAO_SPED"|"ESOCIAL"|"ICMS_ST"} tipo - Obrigatório: SPED, eSocial e ICMS-ST compartilham a
 * mesma tabela no back-end, então sem esse filtro o catálogo vem com os dois tipos misturados.
 * @returns {Promise<{ sucesso: boolean, basesLegais: Array<{ id: number, descricao: string, baseLegalCredito: string|null, fundamentacao: string|null, medidaJudicial: string, tipo: string }> }>}
 */
export function buscarBasesLegais(tipo) {
  return apiRelatorios.get("/relatorios/bases-legais", { params: { tipo } });
}

/* ---------------------------------------------------------------------- */
/* TXM-336 — eSocial                                                       */
/* ---------------------------------------------------------------------- */

/**
 * Detalhe agrupado (comp × rubrica) — GET /relatorios/apuracoes/:id/esocial/agrupado.
 * Sem `params.projeto` os itens já utilizados em projetos aprovados ficam de fora; com ele, a API
 * devolve só os itens daquele projeto (TXM-558).
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [params]
 */
export function buscarAgrupadoEsocial(id, params) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/esocial/agrupado`, {
    params: params?.projeto ? { projeto: params.projeto } : undefined,
  });
}

/**
 * Cards + série por competência — GET /relatorios/apuracoes/:id/esocial/dashboard.
 * `params.projeto` (TXM-558) soma só os itens daquele projeto aprovado.
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [params]
 */
export function buscarDashboardEsocial(id, params) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/esocial/dashboard`, {
    params: params?.projeto ? { projeto: params.projeto } : undefined,
  });
}

/**
 * Catálogo de rúbricas — GET /relatorios/apuracoes/:id/esocial/rubricas.
 * @param {string|number} id
 */
export function buscarRubricasEsocial(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/esocial/rubricas`);
}

/**
 * PATCH .../esocial/rubricas/:codigo — { baseLegalId?, verbaFerias?, flagIncidencia? }.
 * `compoeProjeto` não é aceito: deriva de `baseLegalId` no backend.
 * @param {string|number} id
 * @param {string} codigoRubrica
 * @param {{ baseLegalId?: number|null, verbaFerias?: boolean, flagIncidencia?: boolean }} body
 */
export function atualizarRubricaEsocial(id, codigoRubrica, body) {
  return apiRelatorios.patch(
    `/relatorios/apuracoes/${id}/esocial/rubricas/${encodeURIComponent(codigoRubrica)}`,
    body
  );
}

/**
 * Drill-down bruto por rúbrica — GET /relatorios/apuracoes/:id/esocial/lancamentos.
 * @param {string|number} id
 * @param {{ codigoRubrica: string, pagina?: number, limite?: number, perapurInicio?: string, perapurFim?: string }} params
 */
export function buscarLancamentosEsocial(id, params = {}) {
  const sp = new URLSearchParams();
  const codigoRubrica = String(params.codigoRubrica ?? "").trim();
  if (codigoRubrica) sp.set("codigoRubrica", codigoRubrica);
  if (params.pagina != null) sp.set("page", String(params.pagina));
  if (params.limite != null) sp.set("limit", String(params.limite));
  if (params.perapurInicio) sp.set("perapurInicio", String(params.perapurInicio).trim());
  if (params.perapurFim) sp.set("perapurFim", String(params.perapurFim).trim());
  if (Array.isArray(params.filtrosDinamicos) && params.filtrosDinamicos.length) {
    sp.set("filtrosDinamicos", JSON.stringify(params.filtrosDinamicos));
  }
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/esocial/lancamentos?${sp.toString()}`);
}

/**
 * Cálculo 20S — GET /relatorios/apuracoes/:id/esocial/calculo-20s.
 * @param {string|number} id
 */
export function buscarCalculo20sEsocial(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/esocial/calculo-20s`);
}

/**
 * Modo Sefip (D12) — agregado por competência — GET .../esocial/sefip.
 * @param {string|number} id
 */
export function buscarSefipEsocial(id) {
  return apiRelatorios.get(`/relatorios/apuracoes/${id}/esocial/sefip`);
}

/**
 * Enfileira exportação eSocial (Kafka → api-relatorios-sumarizacao).
 * @param {string|number} id
 * @param {{ tipo: 'agrupado'|'rubricas'|'20s'|'lancamentos', formato?: string, modoXlsx?: string,
 *   filtros?: { codrubr: string[], colunas: string[] } }} body `filtros`: só tipo='rubricas' (TXM-671).
 */
export function exportarEsocial(id, body) {
  return apiRelatorios.post(`/relatorios/apuracoes/${id}/esocial/exportar`, body);
}

/**
 * TXM-336 (T2) — parâmetros globais do Cálculo 20S (sem override por CNPJ).
 */
export function buscarTributariasParametros() {
  // Timeout explícito: evita “Carregando…” indefinido quando auth/DB demoram (~1 min).
  return apiRelatorios.get(`/relatorios/tributarias-parametros`, { timeout: 30000 });
}

/**
 * @param {string} chave
 * @param {number} valor
 */
export function atualizarTributariasParametro(chave, valor) {
  return apiRelatorios.put(`/relatorios/tributarias-parametros/${chave}`, { valor });
}

/**
 * Enfileira exportação de sumarização (Kafka → api-relatorios-sumarizacao).
 * @param {string|number} id
 * @param {{ tipo: 'agrupado'|'lancamentos', filtros?: object }} body
 */
export async function exportarSumarizacaoApuracao(id, body) {
  try {
    const res = await apiRelatorios.post(
      `/relatorios/apuracoes/${id}/sumarizacao/exportar`,
      body
    );
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Não foi possível enfileirar a exportação." };
    }
    return {
      sucesso: true,
      correlationId: res.correlationId,
      nomeArquivo: res.nomeArquivo,
      nomeArquivos: Array.isArray(res.nomeArquivos) ? res.nomeArquivos : [res.nomeArquivo].filter(Boolean),
      status: res.status,
      mensagem: res.mensagem,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao enfileirar exportação da sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-418 — busca o arquivo já gerado por `relatorio.exportar_excel` (worker Go). 404
 * enquanto o processamento não termina; o chamador decide se tenta de novo (polling).
 * @param {string|number} id
 * @param {string} nomeArquivo
 * @returns {Promise<{ sucesso: true, blob: Blob } | { sucesso: false, pronto: boolean, mensagem: string }>}
 */
export async function baixarExportacaoApuracao(id, nomeArquivo) {
  try {
    const blob = await apiRelatorios.get(
      `/relatorios/apuracoes/${id}/sumarizacao/exportacoes/${encodeURIComponent(nomeArquivo)}`,
      { responseType: "blob" }
    );
    return { sucesso: true, blob };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      return { sucesso: false, pronto: false, mensagem: "Arquivo ainda não disponível." };
    }
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao baixar a exportação.";
    return { sucesso: false, pronto: true, mensagem };
  }
}

/**
 * Rastreio de exportação assíncrona — estado do job identificado pelo `correlationId`
 * devolvido no POST de enfileirar (sumarização e eSocial usam o mesmo endpoint).
 * Devolve o corpo cru; a normalização fica em apuracao.service.js.
 * @param {string|number} id
 * @param {string} correlationId
 */
export function consultarStatusExportacao(id, correlationId) {
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/exportacoes/${encodeURIComponent(correlationId)}`
  );
}

/**
 * Baixa o arquivo `indice` (posição em `arquivos[]` do status) de um job concluído.
 * @param {string|number} id
 * @param {string} correlationId
 * @param {number} indice
 * @returns {Promise<Blob>}
 */
export function baixarArquivoExportacao(id, correlationId, indice) {
  return apiRelatorios.get(
    `/relatorios/apuracoes/${id}/exportacoes/${encodeURIComponent(correlationId)}/arquivos/${Number(indice)}`,
    { responseType: "blob" }
  );
}

/**
 * Reprocessa sumarização com novos termos de exclusão (substitui filtros anteriores).
 * @param {string|number} id
 * @param {{ termos_exclusao?: string[], campos?: string[] }} body
 */
export async function reprocessarSumarizacaoApuracao(id, body) {
  try {
    const res = await apiRelatorios.post(
      `/relatorios/apuracoes/${id}/sumarizacao/reprocessar`,
      body
    );
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível reprocessar a sumarização.",
      };
    }
    return {
      sucesso: true,
      mensagem: res.mensagem || "Reprocessamento da sumarização enfileirado.",
      correlationId: res.correlationId,
      sumarizacaoFiltros: res.sumarizacaoFiltros,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar reprocessamento da sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-325 item 08 — reprocessa anos selecionados de uma apuração em divergência
 * (TRUNCATE da z_* do ano + regenera). Disponível só quando status = divergencia.
 * @param {string|number} id
 * @param {{ anos: number[] }} body
 */
export async function reprocessarAnosApuracao(id, body) {
  try {
    const res = await apiRelatorios.post(
      `/relatorios/apuracoes/${id}/reprocessar-anos`,
      body
    );
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível reprocessar os anos selecionados.",
      };
    }
    return {
      sucesso: true,
      mensagem: res.mensagem || "Reprocessamento enfileirado.",
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar reprocessamento por ano.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Dispara sumarização inicial ou retry (apuração concluída, sumarização pendente/erro).
 * @param {string|number} id
 */
export async function processarSumarizacaoApuracao(id) {
  try {
    const res = await apiRelatorios.post(
      `/relatorios/apuracoes/${id}/sumarizacao/processar`,
      {}
    );
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível processar a sumarização.",
      };
    }
    return {
      sucesso: true,
      mensagem:
        res?.mensagem || "Sumarização enfileirada para processamento.",
      correlationId: res?.correlationId,
      sumarizacaoStatus: res?.sumarizacaoStatus,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar processamento da sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Reaplica SELIC nos lançamentos materializados e enfileira reprocesso da sumarização.
 * @param {string|number} id
 */
export async function atualizarSelicApuracao(id) {
  try {
    const res = await apiRelatorios.post(
      `/relatorios/apuracoes/${id}/atualizar-selic`,
      {}
    );
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível atualizar a SELIC.",
      };
    }
    return {
      sucesso: true,
      mensagem:
        res.mensagem ||
        "SELIC atualizada nos lançamentos. Sumarização em processamento.",
      correlationId: res.correlationId,
      sumarizacaoStatus: res.sumarizacaoStatus,
      selic: res.selic,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar atualização de SELIC.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Contas em z_{cnpj}.sped_lista_de_contas (descrição ILIKE %[Conta de Resultado]%) — api-relatorios.
 * Usa o primeiro CNPJ da seleção (somente dígitos na URL).
 * @param {string} cnpj - CNPJ com ou sem máscara
 * @returns {Promise<{ sucesso?: boolean, contas: Array<{ id: string, detalhes: string }> }>}
 */
export function buscarContasSpedListaPorCnpj(cnpj) {
  const digits = String(cnpj ?? "").replace(/\D/g, "");
  if (!digits || digits.length < 11 || digits.length > 14) {
    return Promise.resolve({ sucesso: true, contas: [] });
  }
  return apiRelatorios.get(`/relatorios/sped-lista-contas/${digits}`);
}

export function cadastrarRelatorio(body) {
  return apiRelatorios.post(`/relatorios`, body);
}

export async function exportarRelatorio(body, tipo) {
  let tipoResponse;
  switch (tipo.toLowerCase()) {
    case "rede":
      tipoResponse = "json";
      break;
    case "csv":
    case "excel":
      tipoResponse = "blob";
      break;
    case "ambos":
      tipoResponse = "arraybuffer";
      break;
    default:
      tipoResponse = "json";
      break;
  }

  try {
    const response = await apiRelatorios.post(
      `/relatorios/exportar/${tipo}`,
      {
        ...body,
        nomeRelatorio: body.nomeRelatorio || body.nome,
      },
      { responseType: tipoResponse }
    );

    const noData = {
      mensagem: "Não foi possível localizar informações na base",
    };

    switch (tipo.toLowerCase()) {
      case "csv":
      case "excel":
        return response?.size ? response : noData;
      case "pdf":
        return response?.arquivoPdf ? response : noData;
      case "rede":
        return response;
      case "ambos":
        return response;
      default:
        return response;
    }
  } catch (error) {
    console.error("[ERROR] Erro na exportação:", error);
    throw error;
  }
}

/**
 * Exportação ICMS-ST (contrato dedicado api-relatorios).
 * @param {{ idRelatorio: number, params: { dataInicial: string, dataFinal: string, schema: string, notasSaida: string, prodId: boolean } }} body
 */
export function exportarRelatorioIcmsSt(body) {
  return apiRelatorios.post(`/relatorios/exportar-icms-st`, body);
}

/**
 * Apuração V3 / Conciliação SPED (POST /relatorios/apurar) — tela Apurações.
 * @param {{ idRelatorio: number, cnpj: string, periodo_inicio: string, periodo_fim: string, apuracao?: { contas_criterio?: 'resultado'|'todas' }, sumarizacao?: { termos_exclusao?: string[] } }} body
 */
export function apurarRelatorio(body) {
  return apiRelatorios.post(`/relatorios/apurar`, body);
}

/**
 * Processamento assíncrono genérico via Kafka (POST /relatorios/processar).
 * Body: { idRelatorio, params, configuracoes? } — CSV, ICMS-ST, etc.
 * @param {{ idRelatorio: number, params: object, configuracoes?: Record<string, boolean> }} body
 */
export function processarRelatorio(body) {
  return apiRelatorios.post(`/relatorios/processar`, body);
}

export function atualizarRelatorio(body, idRelatorio) {
  return apiRelatorios.put(`/relatorios/${idRelatorio}`, body);
}

export function excluirRelatorio(idRelatorio) {
  return apiRelatorios.delete(`/relatorios/${idRelatorio}`);
}

//#region ------- Filtros

export function criarFiltro(body) {
  return apiRelatorios.post(`/filtros-salvos/`, body);
}

export function deletaFiltro(idFiltro) {
  return apiRelatorios.delete(`/filtros-salvos/${idFiltro}`);
}

export function atualizaFiltro(idFiltro, body) {
  return apiRelatorios.put(`/filtros-salvos/${idFiltro}`, body);
}

export function trazFiltrosPorIdRelatorio(idRelatorio) {
  return apiRelatorios.get(`/filtros-salvos/${idRelatorio}`);
}

export function trazFiltrosPorIdFiltro(idFiltro) {
  return apiRelatorios.get(`/filtros-salvos/filter/${idFiltro}`);
}

export function buscaFiltroPorNome(searchText) {
  return apiRelatorios.get(`/filtros-salvos/filter/search/${searchText}`);
}

//#region ------- Histórico (desabilitado — relatorios_extraidos)
// TODO(relatorios-extraidos): reativar quando a persistência de snapshots for reavaliada.

/*
export function buscaHistoricoRelatorio(idRelatorio) {
  return apiRelatorios.get(`/historico-relatorios/${idRelatorio}`);
}

export function buscaDadosHistorico(idHistorico) {
  return apiRelatorios.get(`/historico-relatorios/id/${idHistorico}`);
}

export async function downloadHistorico(idHistorico, tipoData) {
  let tipoResponse;
  switch (tipoData) {
    case "csv":
    case "excel":
      tipoResponse = "blob";
      break;
    case "ambos":
      tipoResponse = "arraybuffer";
      break;
    default:
      tipoResponse = "json";
      break;
  }
  const response = await apiRelatorios.get(
    `/historico-relatorios/export/${idHistorico}?tipoData=${tipoData}`,
    {
      responseType: tipoResponse,
    }
  );

  const noData = {
    mensagem: "Não foi possível localizar informações na base",
  };

  switch (tipoData) {
    case "csv":
    case "excel":
      return response.size ? response : noData;
    case "pdf":
      return response?.arquivoPdf ? response : noData;

    default:
      return response;
  }
}

export function buscaHistoricoPorFiltros(idRelatorio, body) {
  return apiRelatorios.post(
    `/historico-relatorios/busca-por-filtros/${idRelatorio}`,
    body
  );
}
*/

//#endregion

export function trazInformacoesFiltros(idRelatorio) {
  return apiRelatorios.get(`/relatorios/traz-info-filtros/${idRelatorio}`);
}

/** TXM-336 — Impostos eSocial V3 (`public.impostos_esocial`). */
export function obterImpostosEsocial({ cnpj, idEmpresa, q } = {}) {
  const sp = new URLSearchParams();
  if (cnpj) sp.set("cnpj", String(cnpj).replace(/\D/g, ""));
  if (idEmpresa != null && idEmpresa !== "") sp.set("idEmpresa", String(idEmpresa));
  if (q != null && String(q).trim() !== "") sp.set("q", String(q).trim());
  const qs = sp.toString();
  return apiRelatorios.get(
    qs ? `/relatorios/impostos-esocial?${qs}` : `/relatorios/impostos-esocial`
  );
}

export function obterImpostosEsocialPorCampoDeBusca(params, textoDeBusca) {
  if (typeof params === "string" || typeof params === "number") {
    return obterImpostosEsocial({
      cnpj: String(params).replace(/\D/g, "").length === 14 ? params : undefined,
      idEmpresa:
        String(params).replace(/\D/g, "").length === 14 ? undefined : params,
      q: textoDeBusca,
    });
  }
  return obterImpostosEsocial({ ...params, q: textoDeBusca });
}

export function cadastrarImpostosEsocial(identificador, body) {
  const payload = { ...body };
  if (identificador != null && typeof identificador === "object") {
    if (identificador.cnpj) payload.cnpj = identificador.cnpj;
    if (identificador.idEmpresa != null) payload.idEmpresa = identificador.idEmpresa;
  } else {
    const digits = String(identificador ?? "").replace(/\D/g, "");
    if (/^\d{14}$/.test(digits)) payload.cnpj = digits;
    else if (identificador != null && identificador !== "") {
      payload.idEmpresa = identificador;
    }
  }
  return apiRelatorios.post(`/relatorios/impostos-esocial`, payload);
}

export function atualizarImpostosEsocial(idImposto, body) {
  return apiRelatorios.put(`/relatorios/impostos-esocial/${idImposto}`, body);
}

export function excluirImpostosEsocial(idImposto) {
  return apiRelatorios.delete(`/relatorios/impostos-esocial/${idImposto}`);
}

export default apiRelatorios;
